import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API, atLeast, useAuth } from './auth';

/**
 * VERAX: Correo de alertas (Administración)
 *
 * 1. Servidor de correo del operador: servidor, puerto, usuario, contraseña,
 *    remitente, administrador general y URL pública. Lo edita el SUPER_ADMIN;
 *    la contraseña se guarda cifrada y nunca vuelve a mostrarse.
 * 2. Destinatarios por empresa: responsable y correos adicionales (ADMIN).
 */

interface Smtp {
  host: string; port: number; secure: boolean; user: string; from: string; replyTo: string;
  operatorEmail: string; publicUrl: string; passwordSet: boolean; configured: boolean;
  source: string; updatedBy: string | null; updatedAt: string | null; ok?: boolean; error?: string;
}
interface Empresa { id: string; name: string; domain: string; responsable: string | null; adicionales: string[]; contacto: string | null; to: string[]; cc: string[] }

const PRESETS: Record<string, { host: string; port: number; secure: boolean }> = {
  'iCloud': { host: 'smtp.mail.me.com', port: 587, secure: false },
  'Google Workspace / Gmail': { host: 'smtp.gmail.com', port: 587, secure: false },
  'Microsoft 365': { host: 'smtp.office365.com', port: 587, secure: false }
};

const input = 'w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 disabled:opacity-60';

export const AlertsPanel: React.FC = () => {
  const { user } = useAuth();
  const canSmtp = atLeast(user, 'SUPER_ADMIN');
  const [smtp, setSmtp] = useState<Smtp | null>(null);
  const [form, setForm] = useState<Partial<Smtp>>({});
  const [password, setPassword] = useState('');
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [edit, setEdit] = useState<Record<string, { responsable: string; adicionales: string }>>({});
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    axios.get(`${API}/alerts/config`).then((r) => {
      setSmtp(r.data.smtp); setForm(r.data.smtp); setPassword('');
      setEmpresas(r.data.empresas);
      setEdit(Object.fromEntries(r.data.empresas.map((e: Empresa) => [e.id, { responsable: e.responsable ?? '', adicionales: (e.adicionales ?? []).join(', ') }])));
    }).catch(() => setMsg({ ok: false, t: 'No se pudo leer la configuración de correo' }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key); setMsg(null);
    try { setMsg({ ok: true, t: await fn() }); load(); }
    catch (e: any) { setMsg({ ok: false, t: e?.response?.data?.error || e?.response?.data?.message || e?.message || 'La operación falló' }); }
    finally { setBusy(null); }
  };

  const guardarSmtp = () => run('smtp', async () => {
    await axios.put(`${API}/alerts/smtp`, { ...form, port: Number(form.port), ...(password ? { password } : {}) });
    return 'Configuración de correo guardada.';
  });
  const borrarClave = () => run('clave', async () => { await axios.put(`${API}/alerts/smtp`, { password: '' }); return 'Contraseña eliminada.'; });
  const guardarEmpresa = (id: string) => run(id, async () => {
    const e = edit[id];
    await axios.put(`${API}/companies/${id}`, {
      adminEmail: e.responsable.trim() || null,
      alertEmails: e.adicionales.split(',').map((x) => x.trim()).filter(Boolean)
    });
    return 'Destinatarios actualizados.';
  });
  const probar = (companyId?: string) => run(`p-${companyId ?? 'g'}`, async () => {
    const r = await axios.post(`${API}/alerts/test`, { companyId });
    if (!r.data.sent) throw new Error(`No se envió: ${r.data.reason}`);
    return `Correo de prueba enviado a ${[...r.data.to, ...r.data.cc].join(', ')}`;
  });

  if (!smtp) return null;
  const set = (k: keyof Smtp, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 mb-6">
      <div className="flex justify-between items-start flex-wrap gap-2 mb-4">
        <div>
          <h3 className="font-semibold">Correo de alertas</h3>
          <p className="text-xs text-gray-400">Cada empresa recibe en su responsable y correos adicionales; el administrador general va en copia de todas.</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded ${smtp.configured && smtp.ok ? 'bg-emerald-900 text-emerald-300' : 'bg-amber-900 text-amber-300'}`}>
          {!smtp.configured ? 'Sin configurar' : smtp.ok ? `Conectado · ${smtp.host}:${smtp.port}` : `Error de conexión: ${smtp.error}`}
        </span>
      </div>

      <h4 className="text-sm font-semibold text-gray-300 mb-2">Servidor de correo</h4>
      {!canSmtp && <p className="text-xs text-gray-500 mb-2">Solo el superadministrador puede cambiar el servidor de correo.</p>}
      <div className="grid md:grid-cols-4 gap-3 mb-3">
        <div className="md:col-span-4 flex gap-2 flex-wrap">
          {canSmtp && Object.entries(PRESETS).map(([n, p]) => (
            <button key={n} type="button" onClick={() => setForm({ ...form, ...p })} className="text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 px-2 py-1 rounded">{n}</button>
          ))}
        </div>
        <label className="md:col-span-2 text-xs text-gray-400">Servidor SMTP
          <input className={input} disabled={!canSmtp} value={form.host ?? ''} onChange={(e) => set('host', e.target.value)} placeholder="smtp.mail.me.com" /></label>
        <label className="text-xs text-gray-400">Puerto
          <input className={input} disabled={!canSmtp} type="number" value={form.port ?? 587} onChange={(e) => set('port', e.target.value)} /></label>
        <label className="text-xs text-gray-400">Cifrado
          <select className={input} disabled={!canSmtp} value={form.secure ? 'tls' : 'starttls'} onChange={(e) => set('secure', e.target.value === 'tls')}>
            <option value="starttls">STARTTLS (587)</option><option value="tls">TLS directo (465)</option>
          </select></label>
        <label className="md:col-span-2 text-xs text-gray-400">Usuario
          <input className={input} disabled={!canSmtp} value={form.user ?? ''} onChange={(e) => set('user', e.target.value)} placeholder="cuenta@dominio.com" autoComplete="off" /></label>
        <label className="md:col-span-2 text-xs text-gray-400">Contraseña {smtp.passwordSet && <span className="text-emerald-400">· guardada (cifrada)</span>}
          <input className={input} disabled={!canSmtp} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={smtp.passwordSet ? '•••••••• (déjelo vacío para conservarla)' : 'contraseña de aplicación'} autoComplete="new-password" /></label>
        <label className="md:col-span-2 text-xs text-gray-400">Remitente
          <input className={input} disabled={!canSmtp} value={form.from ?? ''} onChange={(e) => set('from', e.target.value)} placeholder="alertas@dominio.com" /></label>
        <label className="md:col-span-2 text-xs text-gray-400">Responder a (opcional)
          <input className={input} disabled={!canSmtp} value={form.replyTo ?? ''} onChange={(e) => set('replyTo', e.target.value)} /></label>
        <label className="md:col-span-2 text-xs text-gray-400">Administrador general (copia de todas las alertas)
          <input className={input} disabled={!canSmtp} value={form.operatorEmail ?? ''} onChange={(e) => set('operatorEmail', e.target.value)} placeholder="admin@dominio.com" /></label>
        <label className="md:col-span-2 text-xs text-gray-400">URL de VERAX (enlace en los correos)
          <input className={input} disabled={!canSmtp} value={form.publicUrl ?? ''} onChange={(e) => set('publicUrl', e.target.value)} placeholder="https://verax.dominio.co" /></label>
      </div>
      <div className="flex gap-2 flex-wrap items-center mb-5">
        {canSmtp && <button onClick={guardarSmtp} disabled={!!busy} className="bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-sm px-4 py-2 rounded">{busy === 'smtp' ? 'Guardando…' : 'Guardar'}</button>}
        {canSmtp && smtp.passwordSet && <button onClick={borrarClave} disabled={!!busy} className="bg-gray-700 hover:bg-gray-600 text-sm px-3 py-2 rounded">Borrar contraseña</button>}
        <button onClick={() => probar()} disabled={!!busy} className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-2 rounded">{busy === 'p-g' ? 'Enviando…' : 'Enviar prueba al administrador general'}</button>
        <span className="text-[11px] text-gray-500">Origen: {smtp.source}{smtp.updatedBy ? ` · cambiado por ${smtp.updatedBy}` : ''}</span>
      </div>

      <h4 className="text-sm font-semibold text-gray-300 mb-2">Destinatarios por empresa</h4>
      <div className="space-y-3">
        {empresas.map((e) => (
          <div key={e.id} className="border border-gray-800 rounded p-3">
            <div className="flex justify-between flex-wrap gap-2 mb-2">
              <p className="text-sm font-medium">{e.name} <span className="text-xs text-gray-500">{e.domain}</span></p>
              <p className="text-[11px] text-gray-500">Para: <span className="font-mono">{e.to.join(', ') || '-'}</span>{e.cc.length > 0 && <> · Copia: <span className="font-mono">{e.cc.join(', ')}</span></>}</p>
            </div>
            <div className="grid md:grid-cols-5 gap-2 items-end">
              <label className="md:col-span-2 text-xs text-gray-400">Responsable
                <input className={input} value={edit[e.id]?.responsable ?? ''} placeholder={e.contacto ? `si se deja vacío: ${e.contacto}` : 'responsable@empresa.com'}
                  onChange={(ev) => setEdit({ ...edit, [e.id]: { ...edit[e.id], responsable: ev.target.value } })} /></label>
              <label className="md:col-span-2 text-xs text-gray-400">Correos adicionales (separados por coma)
                <input className={input} value={edit[e.id]?.adicionales ?? ''}
                  onChange={(ev) => setEdit({ ...edit, [e.id]: { ...edit[e.id], adicionales: ev.target.value } })} /></label>
              <div className="flex gap-2">
                <button onClick={() => guardarEmpresa(e.id)} disabled={!!busy} className="bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-xs px-3 py-2 rounded">Guardar</button>
                <button onClick={() => probar(e.id)} disabled={!!busy} className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-xs px-3 py-2 rounded">{busy === `p-${e.id}` ? '…' : 'Probar'}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {msg && <p className={`text-sm mt-3 ${msg.ok ? 'text-emerald-300' : 'text-red-400'}`}>{msg.t}</p>}
    </div>
  );
};
